/**
 * RESPONSIBILITY: hold the role names this installation can assign.
 *
 * The framework ships exactly one — `admin`, the only name it acts on — and an
 * application adds its own on top. It never learns them by importing app code;
 * they arrive here by registration, the same way sections and flags do.
 *
 * WHY A REGISTRY AND NOT A CONSTANT
 *
 * Roles were the one extension point with no registry: sections have `@Section` and
 * `allSections()`, flags have `registerFlag()` and `allFlags()`, roles had a bare
 * enum in app code that nothing in `framework/` could see. That is why the admin
 * endpoint shipped a user list with no role list to assign from — there was no
 * channel for one.
 *
 * Moving the names themselves into the framework would not have fixed it. This
 * repository already has two applications with different sets: the site declares
 * editor/marketing/viewer, and the test suite deliberately declares only editor, so
 * that a framework test does not depend on what one site invented. A framework-owned
 * list would force one on the other.
 *
 *   framework   admin, always, and nothing else
 *   application registerRole('editor'), and whatever else it needs
 */
import { ADMIN_ROLE } from './access';

/**
 * Insertion-ordered, and admin is inserted first — so `admin` heads the list on
 * every screen without anyone sorting it.
 */
const registry = new Set<string>([ADMIN_ROLE]);

/**
 * Returns the name, so call sites can `export const Editor = registerRole('editor')`
 * and keep the declaration and the registration in one expression.
 *
 * Registering the same name twice is a no-op rather than an error: two modules may
 * legitimately mention a role, and the second one is not a mistake.
 */
export function registerRole(name: string): string {
  const trimmed = name.trim();

  if (trimmed === '') {
    throw new Error('A role name cannot be empty.');
  }
  if (trimmed !== name) {
    throw new Error(
      `Role "${name}" has surrounding whitespace. Role names are compared exactly — ` +
        `they are stored in the database and sent over the wire, so " editor" and ` +
        `"editor" would be two different roles that look identical.`,
    );
  }

  registry.add(trimmed);
  return trimmed;
}

/** Every assignable role, admin first. */
export const allRoles = (): string[] => [...registry];

export const isRole = (name: string): boolean => registry.has(name);
